import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { Disc3, Search, CheckCircle2, ChevronLeft, ChevronRight } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import api from "../utils/api";

const LIMIT = 48;

export default function LabelsPage() {
  const { t } = useTranslation();
  const [labels, setLabels]   = useState([]);
  const [total, setTotal]     = useState(0);
  const [search, setSearch]   = useState("");
  const [query, setQuery]     = useState("");
  const [page, setPage]       = useState(1);
  const [sort, setSort]       = useState("release_count");
  const [loading, setLoading] = useState(true);

  const fetchLabels = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: LIMIT, sort });
      if (query.trim()) params.set("search", query.trim());
      const res = await api.get(`/labels/?${params}`);
      setLabels(res.data.labels || []);
      setTotal(res.data.total || 0);
    } catch {}
    setLoading(false);
  }, [page, sort, query]);

  useEffect(() => { fetchLabels(); }, [fetchLabels]);

  const handleSearch = (e) => {
    e.preventDefault();
    setQuery(search);
    setPage(1);
  };

  const pages = Math.ceil(total / LIMIT);

  const SITE     = "https://handsupwillneverdie.de";
  const labelsUrl = `${SITE}/labels`;
  const labelsDesc = "Alle Hands-Up Plattenlabels auf HUWND – gemanaged, verifiziert und mit vollständigen Profilen und Release-Archiv.";

  const itemListJsonLd = JSON.stringify({
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "Hands-Up Plattenlabels auf HUWND",
    "url": labelsUrl,
    "itemListElement": labels.slice(0, 20).map((l, i) => ({
      "@type": "ListItem",
      "position": i + 1,
      "name": l.name,
      "url": `${SITE}/label/${l.slug}`,
    })),
  });
  const breadcrumbJsonLd = JSON.stringify({
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home",   "item": SITE },
      { "@type": "ListItem", "position": 2, "name": "Labels", "item": labelsUrl },
    ],
  });

  return (
    <div className="min-h-screen bg-[#080808] flex flex-col">
      <Helmet>
        <title>Labels | HUWND</title>
        <meta name="description" content={labelsDesc} />
        <link rel="canonical" href={labelsUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="HUWND – Hands Up Will Never Die" />
        <meta property="og:title" content="Labels | HUWND" />
        <meta property="og:description" content={labelsDesc} />
        <meta property="og:url" content={labelsUrl} />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="Labels | HUWND" />
        <meta name="twitter:description" content={labelsDesc} />
        <script type="application/ld+json">{itemListJsonLd}</script>
        <script type="application/ld+json">{breadcrumbJsonLd}</script>
      </Helmet>
      <Header />

      <main className="flex-1 max-w-6xl mx-auto w-full px-4 py-8 space-y-8">

        {/* Page Header */}
        <div className="space-y-1">
          <h1 className="text-4xl sm:text-5xl font-black text-white tracking-tight">Labels</h1>
          <p className="text-zinc-500 text-sm">{t("labels.count_db", { count: total.toLocaleString() })}</p>
        </div>

        {/* Controls */}
        <div className="flex flex-col sm:flex-row gap-3">
          <form onSubmit={handleSearch} className="flex-1 flex gap-2">
            <div className="relative flex-1">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none" />
              <input
                data-testid="labels-search-input"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder={t("labels.search_placeholder")}
                className="w-full pl-9 pr-4 py-2.5 text-sm bg-zinc-900 border border-zinc-800 rounded-xl text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-rose-500/30 transition"
              />
            </div>
            <button data-testid="labels-search-btn" type="submit"
              className="px-4 py-2.5 bg-rose-600 hover:bg-rose-500 text-white text-sm font-semibold rounded-xl transition-colors">
              {t("common.search")}
            </button>
          </form>
          <select value={sort} onChange={e => { setSort(e.target.value); setPage(1); }}
            className="text-sm bg-zinc-900 border border-zinc-800 rounded-xl px-3 py-2.5 text-zinc-300">
            <option value="release_count">{t("labels.sort_most_releases")}</option>
            <option value="name">{t("labels.sort_az")}</option>
          </select>
        </div>

        {/* Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-rose-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : labels.length === 0 ? (
          <div className="text-center py-20 text-zinc-500">{t("labels.none_found")}</div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {labels.map(label => (
              <Link key={label.id} to={`/label/${label.slug}`} data-testid={`label-card-${label.slug}`}
                className="group flex flex-col items-center gap-2 p-3 rounded-2xl bg-zinc-900/60 border border-zinc-800/50 hover:border-zinc-600 hover:bg-zinc-900 transition-all duration-200">
                <div className="w-16 h-16 rounded-xl overflow-hidden bg-zinc-800 flex items-center justify-center flex-shrink-0">
                  {label.logo_url
                    ? <img src={label.logo_url} alt={label.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                    : <Disc3 size={24} className="text-zinc-500" />
                  }
                </div>
                <div className="text-center w-full">
                  <div className="flex items-center justify-center gap-1">
                    <p className="text-xs font-bold text-zinc-100 line-clamp-2 leading-tight">{label.name}</p>
                    {(label.manager_ids?.length > 0) && (
                      <span className="relative group cursor-help flex-shrink-0 inline-flex">
                        <span className="w-4 h-4 rounded-full flex items-center justify-center"
                          style={{
                            background: "linear-gradient(145deg, #34d399 0%, #10b981 45%, #059669 100%)",
                            boxShadow: "0 1px 0 rgba(255,255,255,0.3) inset, 0 -1px 0 rgba(0,0,0,0.2) inset, 0 2px 8px rgba(16,185,129,0.5)",
                          }}>
                          <svg viewBox="0 0 12 12" fill="none" className="w-2 h-2">
                            <path d="M2 6.5L4.5 9L10 3.5" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </span>
                        <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-52 px-3 py-2 text-xs text-zinc-200 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none text-center z-50 leading-relaxed whitespace-normal tracking-normal"
                          style={{ background: "rgba(10,10,10,0.97)", border: "1px solid rgba(255,255,255,0.08)", boxShadow: "0 8px 24px rgba(0,0,0,0.6)" }}>
                          {t("labels.tooltip_managed")}
                        </span>
                      </span>
                    )}
                  </div>
                  <p className="text-[10px] text-zinc-500 mt-0.5">{label.release_count} Releases</p>
                </div>
              </Link>
            ))}
          </div>
        )}

        {/* Pagination */}
        {pages > 1 && (
          <div className="flex items-center justify-center gap-3 pt-4">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
              data-testid="labels-prev-btn"
              className="p-2 bg-zinc-800 rounded-lg disabled:opacity-40 hover:bg-zinc-700 transition-colors text-zinc-300">
              <ChevronLeft size={16} />
            </button>
            <span className="text-sm text-zinc-500">{page} / {pages}</span>
            <button onClick={() => setPage(p => Math.min(pages, p + 1))} disabled={page >= pages}
              data-testid="labels-next-btn"
              className="p-2 bg-zinc-800 rounded-lg disabled:opacity-40 hover:bg-zinc-700 transition-colors text-zinc-300">
              <ChevronRight size={16} />
            </button>
          </div>
        )}

        {/* CTA */}
        <div className="border border-dashed border-zinc-700 rounded-2xl p-6 text-center space-y-3">
          <Disc3 size={28} className="mx-auto text-zinc-500" />
          <p className="text-sm text-zinc-400 font-medium">{t("labels.claim_or_add")}</p>
          <Link to="/label-studio" data-testid="labels-cta-link"
            className="inline-flex items-center gap-2 px-4 py-2 bg-rose-600 hover:bg-rose-500 text-white text-sm font-semibold rounded-xl transition-colors">
            {t("labels.learn_more")} &rarr;
          </Link>
        </div>
      </main>

      <Footer />
    </div>
  );
}
